import React, { useState, useEffect, useRef } from 'react';
import './NewCourseForm.css';
import './NewSemesterImport.css';
import { apiFetch } from '../api';

export default function NewCourseForm({ semesters }) {
  const [formData, setFormData] = useState({
    title: '',
    semester: '',
    courseSemester: '',
    degree: '',
    // Bachelor-Application (ein Feld wie gehabt)
    application: '',
    // Master: mutual-exclusive Felder
    masterApp: '',
    masterDisz: '',
    masterCommon: '',
    weekday: 'Montag',
    timeFrom: '',
    timeTo: '',
    // NEU: Wiederholungstyp
    recurrence: 'weekly', // 'single' | 'weekly' | 'daily'
    // Datumsfelder
    date: '',
    dateFrom: '',
    dateTo: '',
    code: '',
    location: '',
    language: 'deutsch',
    origin: 'TU Clausthal',
    lecturer: '',
    description: '',
    requirement: false,
    excludedDates: '',
  });

  // --- Datenquellen ---
  const applicationOptionsBachelor = [
    'Anwendungsmodul Mobilität',
    'Anwendungsmodul Industrie 4.0',
    'Basismodul Informatik',
    'Basismodul Mathematik',
    'Anwendungsmodul Autonome Systeme',
    'Anwendungsmodul Circular Economy und Umwelttechnik',
    'Anwendungsmodul Digitale Transformation',
    'Anwendungsmodul Energie',
    'Zentrale Veranstaltungen',
    'Projekte und Abschlussarbeit',
  ];

  const masterAppsList = [
    'Anwendungsmodul Autonome Systeme',
    'Anwendungsmodul Circular Economy und Umwelttechnik',
    'Anwendungsmodul Digitale Transformation',
    'Anwendungsmodul Energie',
    'Anwendungsmodul Industrie 4.0',
    'Anwendungsmodul Mobilität',
  ];

  const masterDiszList = [
    'Cooperative Human-Machine Interaction',
    'Engineering Methods and Dependability',
    'Machine Learning and Big Data',
    'Smart Cyber-Physical Systems',
    'Auflage',
  ];

  const commonOptions = [
    'Zentrale Veranstaltung',
    'Projekt',
    'Abschlussarbeit',
  ];

  const languageOptions = ['deutsch', 'englisch'];
  const originOptions   = ['TU Clausthal', 'Ostfalia', 'Digit'];

  // --- Autocomplete ---
  const [titleSuggestions, setTitleSuggestions] = useState([]);
  const suggestionBoxRef = useRef(null);

  const fetchTitleSuggestions = async (q) => {
    if (!q.trim()) return setTitleSuggestions([]);
    try {
      const res = await apiFetch(`autocomplete_course_titles.php?term=${encodeURIComponent(q)}`);
      const data = await res.json();
      setTitleSuggestions(Array.isArray(data) ? data : []);
    } catch {
      setTitleSuggestions([]);
    }
  };

  const onTitleInput = (e) => {
    const v = e.target.value;
    setFormData(prev => ({ ...prev, title: v }));
    fetchTitleSuggestions(v);
  };

  const selectSuggestion = (item) => {
    const app = item.course_application || '';
    setTitleSuggestions([]);
    setFormData(prev => {
      const next = { ...prev, title: item.course_title, degree: item.course_degree || prev.degree };
      if (next.degree === 'Master') {
        // in das passende Master-Feld einsortieren
        if (masterAppsList.includes(app)) { next.masterApp = app; next.masterDisz = ''; next.masterCommon = ''; }
        else if (masterDiszList.includes(app)) { next.masterDisz = app; next.masterApp = ''; next.masterCommon = ''; }
        else if (commonOptions.includes(app)) { next.masterCommon = app; next.masterApp = ''; next.masterDisz = ''; }
      } else {
        next.application = app || prev.application;
      }
      // weitere Felder übernehmen
      next.location    = item.course_location    || prev.location;
      next.lecturer    = item.course_lecturer    || prev.lecturer;
      next.description = item.course_description || prev.description;
      return next;
    });
  };

  // Beim Ändern einzelner Felder
  const handleChange = (field) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setFormData(prev => {
      const upd = { ...prev, [field]: value };
      // Master -> courseSemester auf 1
      if (field === 'degree') {
        if (value === 'Master') {
          upd.courseSemester = '1';
          // Aus Master-Auswahl nichts automatisch setzen, aber Bachelor-Feld leeren
          upd.application = '';
        } else {
          // Wechsel zurück zu Bachelor -> Master-Felder leeren
          upd.masterApp = '';
          upd.masterDisz = '';
          upd.masterCommon = '';
        }
      }
      return upd;
    });
  };

  // Master-Mutual-Exclusive Handler
  const setMasterApp = (v) => setFormData(p => ({ ...p, masterApp: v, masterDisz: '', masterCommon: '' }));
  const setMasterDisz = (v) => setFormData(p => ({ ...p, masterDisz: v, masterApp: '', masterCommon: '' }));
  const setMasterCommon = (v) => setFormData(p => ({ ...p, masterCommon: v, masterApp: '', masterDisz: '' }));

  // optional: Details zu bestehendem Kurs nach Titel nachladen
  useEffect(() => {
    if (!formData.title.trim()) return;
    const fetchCourse = async () => {
      try {
        const res = await apiFetch(`get_course_by_title.php?title=${encodeURIComponent(formData.title)}`);
        const data = await res.json();
        if (!data || data.error) return;
        setFormData(prev => {
          const upd = { ...prev };
          const app = data.course_application || '';
          if (prev.degree === 'Master') {
            if (masterAppsList.includes(app)) { upd.masterApp = app; upd.masterDisz = ''; upd.masterCommon = ''; }
            else if (masterDiszList.includes(app)) { upd.masterDisz = app; upd.masterApp = ''; upd.masterCommon = ''; }
            else if (commonOptions.includes(app)) { upd.masterCommon = app; upd.masterApp = ''; upd.masterDisz = ''; }
          } else {
            upd.application = app || prev.application;
          }
          upd.location    = data.course_location    || prev.location;
          upd.lecturer    = data.course_lecturer    || prev.lecturer;
          upd.description = data.course_description || prev.description;
          return upd;
        });
      } catch {}
    };
    fetchCourse();
  }, [formData.title]); // eslint-disable-line

  // --- Submit ---
  const handleSubmit = async () => {
    // 1) application bestimmen
    const applicationToSave =
      formData.degree === 'Master'
        ? (formData.masterCommon || formData.masterApp || formData.masterDisz || '')
        : formData.application;

    // 2) Validierung
    if (!formData.semester || !formData.title || !formData.degree) {
      alert('Bitte Semester, Titel und Studiengang angeben.');
      return;
    }
    if (!applicationToSave) {
      alert('Bitte ein Anwendungsgebiet, eine Disziplin oder eine Kategorie wählen.');
      return;
    }
    if (!formData.timeFrom || !formData.timeTo) {
      alert('Bitte Beginn und Ende angeben.');
      return;
    }

    if (formData.recurrence === 'single') {
      if (!formData.date) {
        alert('Bitte ein Datum angeben (einmaliger Termin).');
        return;
      }
    } else {
      if (!formData.dateFrom || !formData.dateTo) {
        alert('Bei Wiederholung bitte Von/Bis angeben.');
        return;
      }
    }

    // 3) Payload – nur ein "application" + NEU: recurrence
    const payload = {
      ...formData,
      application: applicationToSave,
      recurrence: formData.recurrence,
      // Zur Rückwärtskompatibilität (Backend-Fallback):
      isWeekly: formData.recurrence === 'weekly'
    };
    // Master-Hilfsfelder nicht mitsenden
    delete payload.masterApp;
    delete payload.masterDisz;
    delete payload.masterCommon;

    try {
      const res = await apiFetch('create_course.php', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      const result = await res.json();
      alert(result.message || result.error || 'Kurs gespeichert');
      if (result.message) window.location.reload();
    } catch (e) {
      alert('Speichern fehlgeschlagen.');
    }
  };

  return (
    <form className="course-form" onSubmit={e => { e.preventDefault(); handleSubmit(); }}>
      <h1>Neuen Kurs anlegen</h1>

      <fieldset>
        <legend>Kursinformationen</legend>
        <div className="form-row">
          <label>Semester (Season):
            <select value={formData.semester} onChange={handleChange('semester')}>
              <option value="">-- wählen --</option>
              {semesters.map(s => (
                <option key={s.id} value={s.short}>{s.name}</option>
              ))}
            </select>
          </label>

          <label>Kurstitel:
            <input type="text" value={formData.title} onChange={onTitleInput} autoComplete="off" />
            {titleSuggestions.length>0 && (
              <ul className="autocomplete-list" ref={suggestionBoxRef}>
                {titleSuggestions.map((item,i) => (
                  <li key={i} onClick={() => selectSuggestion(item)}>{item.course_title}</li>
                ))}
              </ul>
            )}
          </label>
        </div>

        <div className="form-row">
          <label>Studiengang:
            <select value={formData.degree} onChange={handleChange('degree')}>
              <option value="">-- wählen --</option>
              <option value="Bachelor">Bachelor</option>
              <option value="Master">Master</option>
            </select>
          </label>

          <label>Inhaltliches Semester:
            <select value={formData.courseSemester} onChange={handleChange('courseSemester')}>
              <option value="">-- wählen --</option>
              {formData.degree==='Bachelor'
                ? [1,2,3,4,5,6].map(n => <option key={n} value={n}>{n}</option>)
                : [1].map(n => <option key={n} value={n}>{n}</option>)
              }
            </select>
          </label>
        </div>

        {/* Bachelor: ein Feld wie gehabt */}
        {formData.degree !== 'Master' && (
          <div className="form-row">
            <label>Anwendungsmodul:
              <select value={formData.application} onChange={handleChange('application')}>
                <option value="">-- wählen --</option>
                {applicationOptionsBachelor.map(opt => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
                {commonOptions.map(opt => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            </label>

            <label>Dozent*in:
              <input type="text" value={formData.lecturer} onChange={handleChange('lecturer')} />
            </label>
          </div>
        )}

        {/* Master: getrennte Felder – mutual exclusive */}
        {formData.degree === 'Master' && (
          <>
            <div className="form-row">
              <label>Anwendungsgebiet (Master):
                <select value={formData.masterApp} onChange={(e)=>setMasterApp(e.target.value)}>
                  <option value="">-- wählen --</option>
                  {masterAppsList.map(opt => (
                    <option key={opt} value={opt} disabled={!!(formData.masterDisz || formData.masterCommon)}>{opt}</option>
                  ))}
                </select>
              </label>

              <label>Disziplin (Master):
                <select value={formData.masterDisz} onChange={(e)=>setMasterDisz(e.target.value)}>
                  <option value="">-- wählen --</option>
                  {masterDiszList.map(opt => (
                    <option key={opt} value={opt} disabled={!!(formData.masterApp || formData.masterCommon)}>{opt}</option>
                  ))}
                </select>
              </label>
            </div>

            <div className="form-row">
              <label>Kategorie:
                <select value={formData.masterCommon} onChange={(e)=>setMasterCommon(e.target.value)}>
                  <option value="">-- wählen --</option>
                  {commonOptions.map(opt => (
                    <option key={opt} value={opt} disabled={!!(formData.masterApp || formData.masterDisz)}>{opt}</option>
                  ))}
                </select>
              </label>

              <label>Dozent*in:
                <input type="text" value={formData.lecturer} onChange={handleChange('lecturer')} />
              </label>
            </div>
          </>
        )}
      </fieldset>

      {/* Zeit & Ort */}
      <fieldset>
        <legend>Zeit & Ort</legend>
        <div className="form-row">
         <label>Wochentag:
            <select value={formData.weekday} onChange={handleChange('weekday')}>
              {['Montag','Dienstag','Mittwoch','Donnerstag','Freitag','Samstag'].map(d=>(
                <option key={d} value={d}>{d}</option>
              ))}
            </select>
          </label>
          <label>Beginn:
            <input type="time" value={formData.timeFrom} onChange={handleChange('timeFrom')} />
          </label>
          <label>Ende:
            <input type="time" value={formData.timeTo} onChange={handleChange('timeTo')} />
          </label>
        </div>
        <div className="form-row">
          <label>Ort:
            <input type="text" value={formData.location} onChange={handleChange('location')} />
          </label>
          <label>Sprache:
            <select value={formData.language} onChange={handleChange('language')}>
              {languageOptions.map(l=>(
                <option key={l} value={l}>{l}</option>
              ))}
            </select>
          </label>
          <label>Herkunft:
            <select value={formData.origin} onChange={handleChange('origin')}>
              {originOptions.map(o=>(
                <option key={o} value={o}>{o}</option>
              ))}
            </select>
          </label>
        </div>
      </fieldset>

      {/* Beschreibung */}
      <fieldset>
        <legend>Beschreibung</legend>
        <textarea
          value={formData.description}
          onChange={handleChange('description')}
          placeholder="Kursbeschreibung ..."
          rows="5"
        />
      </fieldset>

      {/* Wiederholung */}
      <fieldset>
        <legend>Wiederholung</legend>

        <div className="form-row">
          <label>Art:
            <select value={formData.recurrence} onChange={handleChange('recurrence')}>
              <option value="single">Einmalig (ein Datum)</option>
              <option value="weekly">Wöchentlich</option>
              <option value="daily">Block (täglich)</option>
            </select>
          </label>
        </div>

        {formData.recurrence === 'single' ? (
          <label>Datum:
            <input type="date" value={formData.date} onChange={handleChange('date')} />
          </label>
        ) : (
          <div className="form-row">
            <label>Von:
              <input type="date" value={formData.dateFrom} onChange={handleChange('dateFrom')} />
            </label>
            <label>Bis:
              <input type="date" value={formData.dateTo} onChange={handleChange('dateTo')} />
            </label>
          </div>
        )}

        <label>
          Ausfalltermine (kommagetrennt):
          <input
            type="text"
            value={formData.excludedDates}
            onChange={handleChange('excludedDates')}
            placeholder="z.B. 2025-05-27,2025-06-03"
          />
        </label>
        <small className="example-text">Beispiel: <code>YYYY-MM-DD,YYYY-MM-DD</code></small>
      </fieldset>

      <button type="submit" className="submit-button">Kurs speichern</button>
    </form>
  );
}
